import { useEffect } from "react";
// Type-only import — vite strips this at build time, so lenis (~32 KB) does
// NOT ship in the main chunk. The runtime import happens lazily inside the
// effect below, after first paint, on non-touch desktops only.
import type Lenis from "lenis";

let lenisInstance: Lenis | null = null;

function isTouchDevice() {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

export function SmoothScrollProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || isTouchDevice()) return;

    /* 2026-09-09 — kill switch. Smooth scroll sits underneath every "the page
       feels off" report, and there was no way to take it out of the picture
       without a rebuild. `?nolenis` on any URL skips it entirely: the page then
       uses the browser's native scrolling and nothing else changes. */
    /* Native scrolling is the default now (Isaac, 2026-09-10: "nolenis is
       better for sure"). Lenis stays behind `?lenis` for side-by-side checks. */
    if (typeof window === "undefined" || !new URLSearchParams(window.location.search).has("lenis")) return;

    let cancelled = false;
    let rafId = 0;
    let cleanupAnchor: (() => void) | null = null;

    // Dynamic import — lenis loads after main chunk, asynchronously. Smooth
    // scroll comes online ~100ms after the user can already interact.
    import("lenis").then(({ default: LenisCtor }) => {
      if (cancelled) return;

      const lenis = new LenisCtor({
        /* 2026-09-09 — was `duration: 1.0`, which animates toward a target on a
           fixed clock: a 20-notch flick measured ~1,580ms to settle and the
           last 50px alone took ~500ms, so the page kept sliding for most of a
           second after the hand stopped. `lerp` eases toward the wheel position
           every frame instead, so it tracks the hand and stops when the hand does.
           `easing` stays for the anchor scrollTo below, which passes its own
           duration. */
        lerp: 0.15,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        gestureOrientation: "vertical",
        smoothWheel: true,
      });

      lenisInstance = lenis;

      function raf(time: number) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      const handleAnchorClick = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest("a[href^='#']");
        if (!target) return;
        const hash = target.getAttribute("href");
        if (!hash || hash === "#") return;
        const el = document.querySelector(hash);
        if (!el) return;
        e.preventDefault();
        lenis.scrollTo(el as HTMLElement, {
          offset: -80,
          duration: 1.8,
          easing: (t: number) =>
            t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
        });
      };
      document.addEventListener("click", handleAnchorClick);
      cleanupAnchor = () => document.removeEventListener("click", handleAnchorClick);
    });

    return () => {
      cancelled = true;
      if (cleanupAnchor) cleanupAnchor();
      if (rafId) cancelAnimationFrame(rafId);
      lenisInstance?.destroy();
      lenisInstance = null;
    };
  }, []);

  return <>{children}</>;
}

export function getLenis() {
  return lenisInstance;
}

export function stopLenis() {
  lenisInstance?.stop();
}

export function startLenis() {
  lenisInstance?.start();
}
